import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { deriveOrderNumber } from "./order-number.js";

export const COACH_COMMISSION_LEDGER_CONTRACT_VERSION = "COACH_COMMISSION_LEDGER_V1" as const;

const STABLE_ID_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$";
const MONTH_PATTERN = "^[0-9]{4}-(?:0[1-9]|1[0-2])$";
const BUSINESS_DATE_PATTERN = "^[0-9]{4}-[0-9]{2}-[0-9]{2}$";
const LEDGER_NUMBER_PATTERN = /^TC-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const SOURCE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

const StableIdSchema = Type.String({ minLength: 1, maxLength: 128, pattern: STABLE_ID_PATTERN });
const MonthSchema = Type.String({ pattern: MONTH_PATTERN });
const BusinessDateSchema = Type.String({ pattern: BUSINESS_DATE_PATTERN });
const TimestampSchema = Type.String({ minLength: 24, maxLength: 24 });
const SignedSafeIntegerSchema = Type.Integer({
  minimum: -Number.MAX_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});
const SafeIntegerSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });

export const CoachCommissionLedgerNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^TC-[0-9]{8}-[0-9A-F]{12}$",
});
export type CoachCommissionLedgerNumber = Static<typeof CoachCommissionLedgerNumberSchema>;

export const CoachCommissionLedgerEntryKindSchema = Type.Union([
  Type.Literal("COACH_SERVICE_EARNING"),
  Type.Literal("COACH_SERVICE_REVERSAL"),
  Type.Literal("LOCKED_PERIOD_ADJUSTMENT"),
]);
export type CoachCommissionLedgerEntryKind = Static<typeof CoachCommissionLedgerEntryKindSchema>;

export const CoachCommissionLedgerEntrySchema = Type.Object(
  {
    contractVersion: Type.Literal(COACH_COMMISSION_LEDGER_CONTRACT_VERSION),
    entryId: StableIdSchema,
    entryNumber: CoachCommissionLedgerNumberSchema,
    kind: CoachCommissionLedgerEntryKindSchema,
    coachId: StableIdSchema,
    coachNumber: Type.String({ minLength: 1, maxLength: 80 }),
    coachDisplayName: Type.String({ minLength: 1, maxLength: 80 }),
    coachNameSource: Type.Union([
      Type.Literal("SERVICE_ORDER_LINE"),
      Type.Literal("CURRENT_PROFILE_ZERO_AMOUNT"),
    ]),
    sourceServiceId: StableIdSchema,
    originalServiceId: Type.Union([StableIdSchema, Type.Null()]),
    orderId: StableIdSchema,
    orderBusinessDate: BusinessDateSchema,
    orderNumber: Type.String({ pattern: "^DD-[0-9]{8}-[0-9A-F]{12}$" }),
    sessionId: StableIdSchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    serviceTypeName: Type.String({ minLength: 1, maxLength: 80 }),
    businessDate: BusinessDateSchema,
    earnedMonth: MonthSchema,
    postingMonth: MonthSchema,
    sourceMonthLocked: Type.Boolean(),
    commissionMode: Type.Union([
      Type.Literal("PERCENTAGE"),
      Type.Literal("FIXED"),
      Type.Literal("MONTHLY_TIERED"),
    ]),
    calculationMode: Type.Union([Type.Literal("SOURCE_RULE"), Type.Literal("MONTHLY_TIER_DELTA")]),
    ruleId: StableIdSchema,
    ruleVersion: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    rateBps: Type.Union([Type.Integer({ minimum: 0, maximum: 10_000 }), Type.Null()]),
    fixedCommissionFen: Type.Union([SafeIntegerSchema, Type.Null()]),
    tierCalculationMode: Type.Union([
      Type.Literal("SEGMENTED_PROGRESSIVE"),
      Type.Literal("WHOLE_MONTH"),
      Type.Null(),
    ]),
    effectiveSecondsDelta: SignedSafeIntegerSchema,
    commissionBaseFenDelta: SignedSafeIntegerSchema,
    commissionFenDelta: SignedSafeIntegerSchema,
    reason: Type.Union([Type.String({ minLength: 2, maxLength: 500 }), Type.Null()]),
    sourceOccurredAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type CoachCommissionLedgerEntry = Static<typeof CoachCommissionLedgerEntrySchema>;

export const CoachCommissionLedgerSummarySchema = Type.Object(
  {
    entryCount: SafeIntegerSchema,
    earningEntryCount: SafeIntegerSchema,
    reversalEntryCount: SafeIntegerSchema,
    lockedAdjustmentEntryCount: SafeIntegerSchema,
    positiveCommissionFen: SafeIntegerSchema,
    reversedCommissionFen: SafeIntegerSchema,
    netCommissionFen: SignedSafeIntegerSchema,
  },
  { additionalProperties: false },
);
export type CoachCommissionLedgerSummary = Static<typeof CoachCommissionLedgerSummarySchema>;

export const CoachCommissionLedgerPageSchema = Type.Object(
  {
    contractVersion: Type.Literal(COACH_COMMISSION_LEDGER_CONTRACT_VERSION),
    viewer: Type.Union([Type.Literal("SELF"), Type.Literal("STORE")]),
    selectedCoachId: Type.Union([StableIdSchema, Type.Null()]),
    month: MonthSchema,
    sourceCoverage: Type.Literal("COACH_SERVICE_ONLY"),
    summary: CoachCommissionLedgerSummarySchema,
    entries: Type.Array(CoachCommissionLedgerEntrySchema, { maxItems: 2_000 }),
    generatedAt: TimestampSchema,
    evidence: Type.Union([Type.Literal("GATEWAY_AUTHORITY"), Type.Literal("CLOUD_PROJECTION")]),
  },
  { additionalProperties: false },
);
export type CoachCommissionLedgerPage = Static<typeof CoachCommissionLedgerPageSchema>;

function isRealBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function hasSufficientSourceEntropy(value: string): boolean {
  return (
    SOURCE_ID_PATTERN.test(value) &&
    [...value].filter((character) => /[A-Za-z0-9]/u.test(character)).length >= 12
  );
}

function deriveFortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveCoachCommissionLedgerNumber(
  businessDate: string,
  entryId: string,
): CoachCommissionLedgerNumber {
  if (!isRealBusinessDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!hasSufficientSourceEntropy(entryId)) {
    throw new TypeError("entryId must contain at least 12 safe alphanumeric characters");
  }
  return `TC-${businessDate.replaceAll("-", "")}-${deriveFortyEightBitHex(
    `${businessDate}\0${entryId}`,
  )}`;
}

export function isCoachCommissionLedgerNumber(
  value: unknown,
): value is CoachCommissionLedgerNumber {
  if (!Value.Check(CoachCommissionLedgerNumberSchema, value)) return false;
  const match = LEDGER_NUMBER_PATTERN.exec(value);
  return Boolean(match && isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`));
}

export function isCoachCommissionLedgerEntry(value: unknown): value is CoachCommissionLedgerEntry {
  if (!Value.Check(CoachCommissionLedgerEntrySchema, value)) return false;
  const entry = value as CoachCommissionLedgerEntry;
  let expectedEntryNumber: CoachCommissionLedgerNumber;
  let expectedOrderNumber: string;
  try {
    expectedEntryNumber = deriveCoachCommissionLedgerNumber(entry.businessDate, entry.entryId);
    expectedOrderNumber = deriveOrderNumber(entry.orderBusinessDate, entry.orderId);
  } catch {
    return false;
  }
  const percentageRule =
    entry.commissionMode === "PERCENTAGE" &&
    entry.rateBps !== null &&
    entry.fixedCommissionFen === null &&
    entry.tierCalculationMode === null &&
    entry.calculationMode === "SOURCE_RULE";
  const fixedRule =
    entry.commissionMode === "FIXED" &&
    entry.rateBps === null &&
    entry.fixedCommissionFen !== null &&
    entry.tierCalculationMode === null &&
    entry.calculationMode === "SOURCE_RULE";
  const tieredRule =
    entry.commissionMode === "MONTHLY_TIERED" &&
    entry.rateBps === null &&
    entry.fixedCommissionFen === null &&
    entry.tierCalculationMode !== null &&
    entry.calculationMode === "MONTHLY_TIER_DELTA";
  const earning =
    entry.kind === "COACH_SERVICE_EARNING" &&
    entry.originalServiceId === null &&
    entry.postingMonth === entry.earnedMonth &&
    entry.effectiveSecondsDelta >= 0 &&
    entry.commissionBaseFenDelta >= 0 &&
    entry.commissionFenDelta >= 0;
  const reversal =
    entry.kind === "COACH_SERVICE_REVERSAL" &&
    entry.originalServiceId !== null &&
    entry.postingMonth === entry.earnedMonth &&
    entry.effectiveSecondsDelta <= 0 &&
    entry.commissionBaseFenDelta <= 0 &&
    entry.commissionFenDelta <= 0;
  const lockedAdjustment =
    entry.kind === "LOCKED_PERIOD_ADJUSTMENT" &&
    entry.sourceMonthLocked &&
    entry.postingMonth > entry.earnedMonth;
  return (
    isRealBusinessDate(entry.businessDate) &&
    isRealBusinessDate(entry.orderBusinessDate) &&
    isCanonicalTimestamp(entry.sourceOccurredAt) &&
    entry.entryNumber === expectedEntryNumber &&
    entry.orderNumber === expectedOrderNumber &&
    (percentageRule || fixedRule || tieredRule) &&
    (earning || reversal || lockedAdjustment)
  );
}

export function isCoachCommissionLedgerPage(value: unknown): value is CoachCommissionLedgerPage {
  if (!Value.Check(CoachCommissionLedgerPageSchema, value)) return false;
  const page = value as CoachCommissionLedgerPage;
  if (
    !isCanonicalTimestamp(page.generatedAt) ||
    (page.viewer === "SELF" && page.selectedCoachId === null) ||
    page.entries.some((entry) => !isCoachCommissionLedgerEntry(entry)) ||
    page.entries.some((entry) => entry.postingMonth !== page.month) ||
    (page.selectedCoachId !== null &&
      page.entries.some((entry) => entry.coachId !== page.selectedCoachId))
  ) {
    return false;
  }
  const entryIds = new Set<string>();
  const entryNumbers = new Set<string>();
  for (const [index, entry] of page.entries.entries()) {
    if (entryIds.has(entry.entryId) || entryNumbers.has(entry.entryNumber)) return false;
    entryIds.add(entry.entryId);
    entryNumbers.add(entry.entryNumber);
    const previous = page.entries[index - 1];
    if (
      previous &&
      (previous.sourceOccurredAt < entry.sourceOccurredAt ||
        (previous.sourceOccurredAt === entry.sourceOccurredAt && previous.entryId < entry.entryId))
    ) {
      return false;
    }
  }
  const positiveCommissionFen = page.entries.reduce(
    (total, entry) => total + Math.max(0, entry.commissionFenDelta),
    0,
  );
  const reversedCommissionFen = page.entries.reduce(
    (total, entry) => total + Math.max(0, -entry.commissionFenDelta),
    0,
  );
  if (
    !Number.isSafeInteger(positiveCommissionFen) ||
    !Number.isSafeInteger(reversedCommissionFen)
  ) {
    return false;
  }
  return (
    page.summary.entryCount === page.entries.length &&
    page.summary.earningEntryCount ===
      page.entries.filter((entry) => entry.kind === "COACH_SERVICE_EARNING").length &&
    page.summary.reversalEntryCount ===
      page.entries.filter((entry) => entry.kind === "COACH_SERVICE_REVERSAL").length &&
    page.summary.lockedAdjustmentEntryCount ===
      page.entries.filter((entry) => entry.kind === "LOCKED_PERIOD_ADJUSTMENT").length &&
    page.summary.positiveCommissionFen === positiveCommissionFen &&
    page.summary.reversedCommissionFen === reversedCommissionFen &&
    page.summary.netCommissionFen === positiveCommissionFen - reversedCommissionFen
  );
}
